(function () {
  "use strict";

  const MOBILE_MAX_WIDTH = 767;
  const WRAPPER_SELECTORS = [
    ".product-detail-slider",
    ".yv-product-gallery-slider"
  ];
  const STICKY_CLASS = "gallery--sticky";
  const HIDDEN_CLASS = "gallery--sticky--hide";
  const WRAPPER_CLASS = "tee-sticky-gallery-wrapper";
  const INTEGRATED_CLASS = "tee-gallery-integrated";
  const PREVIEW_SLIDE_CLASS = "tee-gallery-slide";
  const PREVIEW_THUMBNAIL_CLASS = "tee-gallery-preview-thumbnail";
  const PORTAL_CLASS = "tee-sticky-gallery-portal";
  const CLOSE_BUTTON_CLASS = "gallery--close-button";
  const RESTORE_BUTTON_CLASS = "sticky-gallery-button";

  let wrapper = null;
  let gallery = null;
  let artworkForm = null;
  let mainFlickity = null;
  let thumbnailFlickity = null;
  let previewSlide = null;
  let previewThumbnail = null;
  let previewThumbnailViewport = null;
  let stickyPortal = null;
  let stickyPortalViewport = null;
  let closeButton = null;
  let restoreButton = null;
  let frameRequested = false;
  let previewFrameRequested = false;
  let initialized = false;
  let retryTimer = null;
  let observer = null;
  let galleryObserver = null;
  // Same as the KAG designer's floating preview: zoom +/- (up to 3x, drag to look around) and x hides it until the
  // customer scrolls on (about 120px).
  let zoom = 1;
  let pan = { x: 0, y: 0 };
  let closedAt = null;
  let dragged = false;
  const KAG_HEIGHT = function () { return Math.round(Math.min(window.innerHeight * 0.315, 270)); };

  function applyZoom() {
    if (!stickyPortalViewport) return;
    const limit = (zoom - 1) * 50;
    pan.x = Math.max(-limit, Math.min(limit, pan.x));
    pan.y = Math.max(-limit, Math.min(limit, pan.y));
    stickyPortalViewport.style.transform = zoom > 1 ? `translate(${pan.x}%, ${pan.y}%) scale(${zoom})` : "";
    if (stickyPortal) stickyPortal.classList.toggle("is-zoomed", zoom > 1);
  }

  function resetZoom() {
    zoom = 1;
    pan = { x: 0, y: 0 };
    applyZoom();
  }

  function isEligibleViewport() {
    return window.innerWidth <= MOBILE_MAX_WIDTH && window.innerHeight > window.innerWidth;
  }

  function findGalleryWrapper() {
    for (const selector of WRAPPER_SELECTORS) {
      const candidates = document.querySelectorAll(selector);
      for (const candidate of candidates) {
        if (candidate.querySelector("#tee-gallery")) {
          return candidate;
        }
      }
    }
    return null;
  }

  function stripIds(element) {
    if (element.id) {
      element.removeAttribute("id");
    }
    element.querySelectorAll("[id]").forEach(function (child) {
      child.removeAttribute("id");
    });
  }

  function getPreviewSource() {
    if (!gallery) {
      return null;
    }
    return (
      gallery.querySelector(".tee-slide--active .tee-mockup") ||
      gallery.querySelector(".tee-mockup")
    );
  }

  function renderScaledPreview(
    target,
    source,
    sourceClass,
    maximumScale,
    desiredArtworkFill
  ) {
    if (!target || !source) {
      return null;
    }

    const sourceRect = source.getBoundingClientRect();
    if (!sourceRect.width || !sourceRect.height) {
      return null;
    }

    const clone = source.cloneNode(true);
    stripIds(clone);
    clone.setAttribute("aria-hidden", "true");
    clone.style.width = `${sourceRect.width}px`;
    clone.style.height = `${sourceRect.height}px`;

    const sourceWrapper = document.createElement("span");
    sourceWrapper.className = sourceClass;
    sourceWrapper.style.width = `${sourceRect.width}px`;
    sourceWrapper.style.height = `${sourceRect.height}px`;
    sourceWrapper.appendChild(clone);
    target.replaceChildren(sourceWrapper);

    const availableWidth = target.clientWidth;
    const availableHeight = target.clientHeight;
    if (!availableWidth || !availableHeight) {
      return null;
    }

    let scale = Math.min(
      maximumScale,
      availableWidth / sourceRect.width,
      availableHeight / sourceRect.height
    );
    if (desiredArtworkFill) {
      const artwork = source.querySelector(".tee-printarea--artwork");
      if (artwork) {
        const artworkRect = artwork.getBoundingClientRect();
        const artworkRatio = Math.max(
          artworkRect.width / sourceRect.width,
          artworkRect.height / sourceRect.height
        );
        if (artworkRatio) {
          scale *= Math.min(
            1.28,
            Math.max(1, desiredArtworkFill / artworkRatio)
          );
        }
      }
    }
    const left = (availableWidth - sourceRect.width * scale) / 2;
    const top = (availableHeight - sourceRect.height * scale) / 2;
    sourceWrapper.style.transform = `translate(${left}px, ${top}px) scale(${scale})`;

    return {
      height: sourceRect.height * scale,
      width: sourceRect.width * scale
    };
  }

  function refreshPreviewCopies() {
    previewFrameRequested = false;
    const source = getPreviewSource();
    if (!source) {
      return;
    }

    renderScaledPreview(
      previewThumbnailViewport,
      source,
      `${PREVIEW_THUMBNAIL_CLASS}__source`,
      1
    );

    if (
      stickyPortalViewport &&
      isEligibleViewport() &&
      document.body.classList.contains(STICKY_CLASS)
    ) {
      const maximumHeight = KAG_HEIGHT() - 16;
      stickyPortalViewport.style.height = `${maximumHeight}px`;
      const size = renderScaledPreview(
        stickyPortalViewport,
        source,
        `${PORTAL_CLASS}__source`,
        0.78,
        0.9
      );
      if (size) {
        stickyPortal.style.setProperty(
          "--tee-sticky-height",
          `${maximumHeight + 16}px`
        );
        stickyPortal.classList.add("is-ready");
      }
    }
  }

  function schedulePreviewRefresh() {
    if (previewFrameRequested) {
      return;
    }
    previewFrameRequested = true;
    window.requestAnimationFrame(refreshPreviewCopies);
  }

  function clearStickyState() {
    document.body.classList.remove(STICKY_CLASS, HIDDEN_CLASS);
  }

  function updateStickyState() {
    frameRequested = false;

    if (!isEligibleViewport() || !wrapper || !artworkForm || !stickyPortal) {
      clearStickyState();
      return;
    }

    const galleryBox = wrapper.getBoundingClientRect();
    const formBottom = artworkForm.getBoundingClientRect().bottom;
    const portalHeight = stickyPortal.getBoundingClientRect().height || KAG_HEIGHT();
    // Like the KAG floating preview: only once the gallery has scrolled away (under the pop-up), while the form is on screen.
    const shouldStick = galleryBox.bottom < KAG_HEIGHT() && formBottom > portalHeight;
    if (closedAt !== null && Math.abs(window.scrollY - closedAt) > 120) {
      closedAt = null;
      document.body.classList.remove(HIDDEN_CLASS);
    }

    const wasSticky = document.body.classList.contains(STICKY_CLASS);
    document.body.classList.toggle(STICKY_CLASS, shouldStick);
    if (shouldStick && !wasSticky) {
      stickyPortal.classList.remove("is-ready");
      window.requestAnimationFrame(schedulePreviewRefresh);
    }
    if (!shouldStick) {
      document.body.classList.remove(HIDDEN_CLASS);
      closedAt = null;
      stickyPortal.classList.remove("is-ready");
      resetZoom();
    }
  }

  function scheduleUpdate() {
    if (frameRequested) {
      return;
    }
    frameRequested = true;
    window.requestAnimationFrame(updateStickyState);
  }

  function createStickyPortal() {
    if (stickyPortal) {
      return;
    }

    stickyPortal = document.createElement("div");
    stickyPortal.className = PORTAL_CLASS;
    stickyPortal.setAttribute("aria-label", "Live personalized preview");

    stickyPortalViewport = document.createElement("div");
    stickyPortalViewport.className = `${PORTAL_CLASS}__viewport`;
    stickyPortal.appendChild(stickyPortalViewport);

    const icon = function (path) { return `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true">${path}</svg>`; };
    const button = function (className, label, path, onClick) {
      const node = document.createElement("button");
      node.type = "button";
      node.className = `tee-sticky-gallery-button ${className}`;
      node.setAttribute("aria-label", label);
      node.innerHTML = icon(path);
      node.addEventListener("click", function (event) { event.preventDefault(); event.stopPropagation(); onClick(); });
      stickyPortal.appendChild(node);
      return node;
    };
    closeButton = button(CLOSE_BUTTON_CLASS, "Hide preview", '<path d="M6 6l12 12M18 6L6 18"/>', function () {
      closedAt = window.scrollY;
      resetZoom();
      document.body.classList.add(HIDDEN_CLASS);
    });
    button("tee-sticky-gallery-zoom-in", "Zoom in", '<circle cx="11" cy="11" r="7"/><path d="M11 8v6M8 11h6M20 20l-4-4"/>', function () { zoom = Math.min(3, zoom + 0.5); applyZoom(); });
    button("tee-sticky-gallery-zoom-out", "Zoom out", '<circle cx="11" cy="11" r="7"/><path d="M8 11h6M20 20l-4-4"/>', function () { zoom = Math.max(1, zoom - 0.5); if (zoom === 1) pan = { x: 0, y: 0 }; applyZoom(); });

    // Zoomed in, drag to look around the design.
    let drag = null;
    stickyPortal.addEventListener("pointerdown", function (event) {
      if (zoom <= 1 || event.target.closest("button")) return;
      drag = { x: event.clientX, y: event.clientY, pan: { x: pan.x, y: pan.y } };
      dragged = false;
      stickyPortal.setPointerCapture?.(event.pointerId);
    });
    stickyPortal.addEventListener("pointermove", function (event) {
      if (!drag) return;
      const box = stickyPortal.getBoundingClientRect();
      const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
      if (Math.abs(dx) + Math.abs(dy) > 4) dragged = true;
      pan = { x: drag.pan.x + (dx / box.width) * 100, y: drag.pan.y + (dy / box.height) * 100 };
      applyZoom();
    });
    const endDrag = function () { drag = null; };
    stickyPortal.addEventListener("pointerup", endDrag);
    stickyPortal.addEventListener("pointercancel", endDrag);

    stickyPortal.addEventListener("click", function (event) {
      // Buttons and zoomed-in dragging do not open the large view.
      if (event.target.closest("button") || zoom > 1 || dragged) {
        dragged = false;
        return;
      }
      const source = getPreviewSource();
      if (source) {
        source.click();
      }
    });
    document.body.appendChild(stickyPortal);

    restoreButton = document.createElement("button");
    restoreButton.type = "button";
    restoreButton.className = RESTORE_BUTTON_CLASS;
    restoreButton.setAttribute("aria-label", "Show live preview");
    restoreButton.addEventListener("click", function (event) {
      event.preventDefault();
      event.stopPropagation();
      document.body.classList.remove(HIDDEN_CLASS);
      schedulePreviewRefresh();
      scheduleUpdate();
    });
    document.body.appendChild(restoreButton);
  }

  function createPreviewSlide() {
    previewSlide = document.createElement("div");
    previewSlide.className = [
      "yv-product-slider-item",
      "yv-product-image-item",
      "big__slider",
      "flickity-slider-item",
      PREVIEW_SLIDE_CLASS
    ].join(" ");
    previewSlide.setAttribute("data-tee-gallery-slide", "");
    previewSlide.appendChild(gallery);
    mainFlickity.prepend(previewSlide);
  }

  function createPreviewThumbnail() {
    previewThumbnail = document.createElement("div");
    previewThumbnail.className = [
      "yv-product-slider-item",
      "gallery__slide-img",
      PREVIEW_THUMBNAIL_CLASS
    ].join(" ");
    previewThumbnail.setAttribute("data-tee-gallery-thumbnail", "");
    previewThumbnail.setAttribute("role", "button");
    previewThumbnail.setAttribute("tabindex", "0");
    previewThumbnail.setAttribute("aria-label", "Show live personalized preview");

    previewThumbnailViewport = document.createElement("span");
    previewThumbnailViewport.className = `${PREVIEW_THUMBNAIL_CLASS}__viewport`;
    previewThumbnail.appendChild(previewThumbnailViewport);

    function selectPreview() {
      mainFlickity.select(0);
    }

    previewThumbnail.addEventListener("click", selectPreview);
    previewThumbnail.addEventListener("keydown", function (event) {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        selectPreview();
      }
    });
    thumbnailFlickity.prepend(previewThumbnail);
  }

  function refreshFlickityLink() {
    mainFlickity.resize();
    mainFlickity.reposition();
    thumbnailFlickity.resize();
    thumbnailFlickity.reposition();
    if (typeof thumbnailFlickity.navCompanionSelect === "function") {
      thumbnailFlickity.navCompanionSelect();
    }
    mainFlickity.select(0, false, true);
  }

  function integrateGallery() {
    wrapper.classList.remove("tee-gallery-unified", "tee-gallery-preview-active");
    wrapper.classList.add(WRAPPER_CLASS, INTEGRATED_CLASS);

    createPreviewSlide();
    createPreviewThumbnail();
    createStickyPortal();
    refreshFlickityLink();

    mainFlickity.on("select", function () {
      scheduleUpdate();
    });

    thumbnailFlickity.element.addEventListener("click", function (event) {
      const thumbnail = event.target.closest(".yv-product-slider-item");
      if (!thumbnail) {
        return;
      }
      const index = thumbnailFlickity.cells.findIndex(function (cell) {
        return cell.element === thumbnail;
      });
      if (index >= 0) {
        mainFlickity.select(index);
      }
    });

    artworkForm.addEventListener("input", function () {
      mainFlickity.select(0);
      schedulePreviewRefresh();
    });
    artworkForm.addEventListener("change", function () {
      mainFlickity.select(0);
      schedulePreviewRefresh();
    });

    galleryObserver = new MutationObserver(schedulePreviewRefresh);
    galleryObserver.observe(gallery, {
      attributes: true,
      characterData: true,
      childList: true,
      subtree: true
    });

    schedulePreviewRefresh();
    scheduleUpdate();
  }

  function initialize() {
    if (initialized) {
      return true;
    }
    if (!document.body.classList.contains("teeinblue-enabled")) {
      return false;
    }

    const nextWrapper = findGalleryWrapper();
    if (!nextWrapper || !window.Flickity || typeof window.Flickity.data !== "function") {
      return false;
    }

    const nextGallery = nextWrapper.querySelector("#tee-gallery");
    const nextArtworkForm = document.getElementById("tee-artwork-form");
    const nextMainSlider = nextWrapper.querySelector(".yv-product-big-slider");
    const nextThumbnailSlider = nextWrapper.querySelector(".yv-product-small-slider");
    const nextMainFlickity = nextMainSlider && window.Flickity.data(nextMainSlider);
    const nextThumbnailFlickity =
      nextThumbnailSlider && window.Flickity.data(nextThumbnailSlider);

    if (
      !nextGallery ||
      !nextArtworkForm ||
      !nextMainSlider ||
      !nextThumbnailSlider ||
      !nextMainFlickity ||
      !nextThumbnailFlickity
    ) {
      return false;
    }

    wrapper = nextWrapper;
    gallery = nextGallery;
    artworkForm = nextArtworkForm;
    mainFlickity = nextMainFlickity;
    thumbnailFlickity = nextThumbnailFlickity;
    initialized = true;
    integrateGallery();
    return true;
  }

  function attemptInitialize() {
    if (initialize()) {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      if (retryTimer) {
        window.clearTimeout(retryTimer);
        retryTimer = null;
      }
      return;
    }

    if (!observer && document.body) {
      observer = new MutationObserver(attemptInitialize);
      observer.observe(document.body, { childList: true, subtree: true });
    }

    if (!retryTimer) {
      retryTimer = window.setTimeout(function () {
        retryTimer = null;
        attemptInitialize();
      }, 250);
    }
  }

  window.addEventListener("scroll", scheduleUpdate, { passive: true });
  window.addEventListener("resize", function () {
    schedulePreviewRefresh();
    scheduleUpdate();
  }, { passive: true });
  document.addEventListener("teeinblue-event-component-injected", attemptInitialize);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", attemptInitialize);
  } else {
    attemptInitialize();
  }
})();

// Teeinblue's preview window ("Close preview") gets the same check-and-buy footer as the KAG preview window:
// "Looks good to go?" above the design and an Add to cart button, which closes the window and presses the page's own
// Add to Cart (so Teeinblue still checks and saves the personalization before the item goes to the cart).
(() => {
  "use strict";
  const DIALOG = ".tee-dialog-product-mockup";
  const pageCartButton = () => {
    const buttons = [...document.querySelectorAll('form[action*="/cart/add"] button[name="add"], form[action*="/cart/add"] [type="submit"]')];
    return buttons.find(button => !button.closest(DIALOG) && button.getBoundingClientRect().height > 0) || buttons.find(button => !button.closest(DIALOG)) || null;
  };
  const enhance = dialog => {
    const content = dialog.querySelector(".tee-dcontent"), body = content?.querySelector(".tee-dcontent-body"), footer = content?.querySelector(".tee-dcontent-footer");
    if (!content || !body || !footer || content.dataset.kooleztEnhanced) return;
    content.dataset.kooleztEnhanced = "1";
    const head = document.createElement("div");
    head.className = "koolezt-tee-preview-head";
    head.innerHTML = '<p class="koolezt-tee-preview-head__title">Looks good to go?</p><p class="koolezt-tee-preview-head__note">Please double-check your design before adding it to your cart.</p>';
    body.before(head);
    const close = footer.querySelector("button");
    const cart = document.createElement("button");
    cart.type = "button";
    cart.className = "koolezt-tee-preview-cart";
    cart.textContent = "Add to cart";
    const source = pageCartButton();
    if (source) {
      const style = getComputedStyle(source);
      if (style.backgroundColor && style.backgroundColor !== "rgba(0, 0, 0, 0)") cart.style.background = style.backgroundColor;
      if (style.color) cart.style.color = style.color;
    }
    cart.addEventListener("click", () => {
      const target = pageCartButton();
      close?.click();
      window.setTimeout(() => target?.click(), 150);
    });
    footer.classList.add("koolezt-tee-preview-footer");
    footer.prepend(cart);
  };
  const scan = () => document.querySelectorAll(DIALOG).forEach(enhance);
  new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
  scan();
})();

// Teeinblue's paid Yes/No add-ons ("Shipping Protection +$14.99" with Yes / No buttons) shown as a card like
// TrendingCustom's: a picture, the name and price, the description and one checkbox. The checkbox presses Teeinblue's
// own Yes / No, so Teeinblue still handles the price and the cart. Pictures come from Theme settings → Koolezt add-on
// pictures (matched by name); without one, a simple icon is shown.
(() => {
  "use strict";
  const ICONS = [
    [/protect|insur/i, '<path d="M12 3 4 6v6c0 4.5 3.4 8.3 8 9 4.6-.7 8-4.5 8-9V6l-8-3Z"/><path d="m8.5 12 2.5 2.5 4.5-5"/>'],
    [/led|light|backlit|lamp/i, '<path d="M9 18h6M10 21h4"/><path d="M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2V16h5v-.1c0-.8.4-1.5 1-2A6 6 0 0 0 12 3Z"/>'],
    [/usa|america|made in/i, '<path d="M5 21V4"/><path d="M5 4h12l-2 4 2 4H5"/>'],
    [/stake|ground|garden/i, '<path d="M12 3v18M8 7h8M10 21h4"/><path d="M12 21l-2-4h4l-2 4"/>'],
    [/gift|box|wrap|card/i, '<rect x="3" y="8" width="18" height="13" rx="1"/><path d="M12 8v13M3 12h18M12 8c-2-4-6-4-6-1s4 1 6 1Zm0 0c2-4 6-4 6-1s-4 1-6 1Z"/>'],
  ];
  const icon = name => (ICONS.find(([test]) => test.test(name)) || [null, '<path d="M12 5v14M5 12h14"/>'])[1];
  const picture = name => (Array.isArray(window.kooleztAddonPictures) ? window.kooleztAddonPictures : []).find(item => item && item.name && item.url && name.toLowerCase().includes(String(item.name).toLowerCase().trim()))?.url || null;
  const priced = /^(.*?)\s*\+\s*(\$\s*[\d.,]+)\s*$/;
  // Teeinblue's own answers: after a reload it restores the last answers (and charges for them) without marking its
  // Yes / No on the page, so the cards follow these answers rather than the buttons.
  const teeAnswer = name => { try { return window.teeinblue?.getCurrentCustomization?.()?.[name]; } catch (e) { return undefined; } };
  const syncs = new Set();

  const build = field => {
    if (field.querySelector(".koolezt-addon-card")) return;
    const heading = field.querySelector(".tee-field__heading")?.textContent.trim() || "", match = heading.match(priced);
    if (!match) return;
    const radios = [...field.querySelectorAll('input[type="radio"]')], label = radio => field.querySelector(`label[for="${radio.id}"]`)?.textContent.trim() || "";
    const yes = radios.find(radio => /^yes\b/i.test(label(radio))), no = radios.find(radio => /^no\b/i.test(label(radio)));
    if (!yes || !no || radios.length !== 2) return;
    const name = match[1].trim(), price = match[2].replace(/\s+/g, ""), hint = field.querySelector(".tee-input-hint")?.textContent.trim() || "", url = picture(name);
    const card = document.createElement("label");
    card.className = "koolezt-addon-card";
    card.innerHTML = `<span class="koolezt-addon-card__media">${url ? `<img src="${url}" alt="" loading="lazy">` : `<svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${icon(name)}</svg>`}</span><span class="koolezt-addon-card__text"><span class="koolezt-addon-card__title"></span><span class="koolezt-addon-card__price"></span><span class="koolezt-addon-card__hint"></span><span class="koolezt-addon-card__choose"><input type="checkbox"><span>Add to my order</span></span></span>`;
    card.querySelector(".koolezt-addon-card__title").textContent = name;
    card.querySelector(".koolezt-addon-card__price").textContent = `+${price}`;
    card.querySelector(".koolezt-addon-card__hint").textContent = hint;
    const box = card.querySelector("input");
    // Teeinblue shows the price at once but records the answer about half a second later, so right after a click the
    // card keeps the customer's choice until the recorded answer agrees (at most 2 seconds).
    let wanted = null, wantedUntil = 0;
    const sync = () => {
      const answer = teeAnswer(yes.name), recorded = answer === undefined || answer === null || answer === "" ? yes.checked : answer === yes.value;
      if (wanted !== null && (recorded === wanted || Date.now() > wantedUntil)) wanted = null;
      const on = wanted ?? recorded;
      box.checked = on; card.classList.toggle("is-on", on);
    };
    const follow = () => (field.isConnected ? sync() : syncs.delete(follow));
    syncs.add(follow);
    // Press Teeinblue's own label: clicking the radio itself ticks it on screen but Teeinblue does not take the answer,
    // so the add-on would not be added to the cart.
    const choose = radio => (field.querySelector(`label[for="${radio.id}"]`) || radio).click();
    box.addEventListener("change", () => { wanted = box.checked; wantedUntil = Date.now() + 2000; card.classList.toggle("is-on", wanted); choose(wanted ? yes : no); window.setTimeout(sync, 0); });
    radios.forEach(radio => radio.addEventListener("change", sync));
    field.classList.add("koolezt-addon-field");
    field.append(card);
    sync();
  };
  const scan = () => document.querySelectorAll(".tee-field").forEach(build);
  new MutationObserver(() => window.requestAnimationFrame(scan)).observe(document.documentElement, { childList: true, subtree: true });
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", scan); else scan();
  window.setInterval(() => syncs.forEach(run => run()), 700);
})();

// Phones: the action box (Preview + Add to cart) floats at the bottom of the screen while the options are on screen.
// While it floats it shows Preview only (the Preview window has its own Add to cart); in its own place under the
// options it shows both. html.koolezt-actions-floating marks the floating state (the CSS hides Add to cart then).
(() => {
  "use strict";
  const PREVIEW = ".tee-btn--preview", FLOATING = "koolezt-actions-floating";
  let full = 0, line = null, queued = false;
  const update = () => {
    queued = false;
    const html = document.documentElement, box = document.querySelector(PREVIEW)?.closest(".pdp-action-container");
    if (!box || getComputedStyle(box).position !== "sticky") { html.classList.remove(FLOATING); return; }
    let mark = box.nextElementSibling;
    if (!mark || !mark.classList.contains("koolezt-actions-mark")) { mark = document.createElement("div"); mark.className = "koolezt-actions-mark"; mark.setAttribute("aria-hidden", "true"); box.after(mark); }
    // The box floats when it sits above its own place (the mark right after it), whatever the screen height says.
    const style = getComputedStyle(box), now = box.getBoundingClientRect();
    const ownBottomNow = mark.getBoundingClientRect().top - (parseFloat(style.marginBottom) || 0), lifted = ownBottomNow - now.bottom > 2;
    if (lifted) line = now.bottom + (parseFloat(style.bottom) || 0);
    if (!html.classList.contains(FLOATING)) { full = box.offsetHeight; html.classList.toggle(FLOATING, lifted); return; }
    // Floating (Add to cart hidden): it stays so while the box, with Add to cart back, would still be off its own place.
    const ownBottom = ownBottomNow + Math.max(0, full - box.offsetHeight);
    html.classList.toggle(FLOATING, ownBottom > (line ?? window.innerHeight) + 1);
  };
  const queue = () => { if (!queued) { queued = true; window.requestAnimationFrame(update); } };
  window.addEventListener("scroll", queue, { passive: true });
  window.addEventListener("resize", () => { line = null; queue(); });
  window.setInterval(queue, 1000);
  queue();
})();
